/**
 * Single source of truth for "how long is this job listing valid for" — reconciles three
 * signals (the source's own applicationDeadline, postedDate, and how recently our crawler
 * actually reconfirmed the ad, lastSeen) that used to be computed independently by
 * get-job-listing-http-status-code.ts (410 vs 200), generate-job-posting-schema.ts
 * (JobPosting `validThrough`) and lambda/maintainer.ts (the isActive flag) — and could disagree:
 * a job could render 200 while its schema already claimed it had expired.
 */
// Namespace import + unwrap: lambda/ has no "type": "module" (see server/utils/lambda-interop.ts).
import { unwrapLambdaModule } from '../../server/utils/lambda-interop';
import * as parseLastSeenNs from '../../lambda/utils/parse-last-seen-to-ms';
const { parseLastSeenToUnixMs } = unwrapLambdaModule(parseLastSeenNs);

const DAY_MS = 24 * 60 * 60 * 1000;

/** How long a listing is assumed to stay open when the source gives no applicationDeadline. */
export const NO_DEADLINE_VALID_DAYS = 35;

/**
 * Once a listing's nominal end date (applicationDeadline, or the no-deadline fallback above) has
 * passed, how much further we trust it once the crawler has actually seen it again at or after
 * that date — i.e. proof the source hasn't taken it down despite the nominal date passing.
 */
export const RECONFIRMED_GRACE_DAYS = 30;

export interface JobListingValidityInput {
  postedDate?: string | number;
  lastSeen?: string | number;
  applicationDeadline?: string | number;
}

function toEpochMs(value: string | number | undefined): number | undefined {
  if (value === undefined || value === null) return undefined;
  const ms = typeof value === 'string' ? Date.parse(value) : value;
  return typeof ms === 'number' && !isNaN(ms) ? ms : undefined;
}

/**
 * The timestamp (ms) through which `job` is considered a valid, live listing.
 *
 * - Base expectation: `applicationDeadline` if the source gave one, else `NO_DEADLINE_VALID_DAYS`
 *   after `postedDate` (falls back further to `lastSeen`, then `nowMs`, only when postedDate
 *   itself is missing/unparseable — so a job is never spuriously killed by absent data).
 * - If that base date has already passed, but `lastSeen` is at or after it, the base date was
 *   simply wrong for this listing (a stale/rolling deadline, or one that quietly outlives the
 *   35-day norm): trust the crawl instead and extend validity `RECONFIRMED_GRACE_DAYS` past that
 *   last confirmed sighting. Otherwise the base date stands, whether that means "still valid,
 *   nominal date hasn't arrived yet" or "genuinely expired, nothing contradicts it".
 */
export function computeJobListingValidThroughMs(job: JobListingValidityInput, nowMs: number): number {
  const lastSeenMs = parseLastSeenToUnixMs(job.lastSeen);
  const deadlineMs = toEpochMs(job.applicationDeadline);
  const postedMs = toEpochMs(job.postedDate);

  const noDeadlineFallbackMs = NO_DEADLINE_VALID_DAYS * DAY_MS;
  const baseMs =
    deadlineMs ??
    (postedMs !== undefined
      ? postedMs + noDeadlineFallbackMs
      : (lastSeenMs ?? nowMs) + noDeadlineFallbackMs);

  if (lastSeenMs !== undefined && lastSeenMs >= baseMs) {
    return lastSeenMs + RECONFIRMED_GRACE_DAYS * DAY_MS;
  }
  return baseMs;
}
